<template>
  <div class="page-attendants-day">
    <aside class="attendants-day-date-sidebar" aria-label="Calendario de agendamentos">
      <div class="attendants-day-mini-calendar">
        <div class="attendants-day-sidebar-title">{{ dayOfWeekText() }}</div>
        <input ref="desktopCalendar" type="text" class="desktop-calendar-input" aria-label="Selecione a data" />
      </div>
      <div class="attendants-day-daily-metrics" aria-label="Resumo do dia selecionado">
        <div class="attendants-day-metrics-title">Resumo do dia</div>
        <div class="attendants-day-metric-card attendants-day-metric-card--primary">
          <span>Agendamentos</span>
          <strong>{{ dayMetrics.totalAppointments }}</strong>
        </div>
        <div class="attendants-day-metric-card">
          <span>Previsto no dia</span>
          <strong>{{ formatCurrency(dayMetrics.totalValue) }}</strong>
        </div>
        <div class="attendants-day-metric-card">
          <span>Clientes atendidos</span>
          <strong>{{ dayMetrics.uniqueClients }}</strong>
        </div>
        <div class="attendants-day-metric-card">
          <span>Horas ocupadas</span>
          <strong>{{ formatDuration(dayMetrics.totalMinutes) }}</strong>
        </div>
        <p v-if="dayMetrics.blockedCount" class="attendants-day-metrics-note">
          {{ dayMetrics.blockedCount }} bloqueio{{ dayMetrics.blockedCount > 1 ? 's' : '' }} na agenda
        </p>
      </div>
    </aside>

    <!-- date picker + prev/next buttons + title -->
    <div class="attendants-day-weekday text-uppercase fw-bold mb-1 d-flex justify-content-center"
      style="font-size: 0.9rem; letter-spacing: 1px;">
      {{ dayOfWeekText() }}
    </div>
    <div class="attendants-day-date-controls d-flex align-items-center mb-2">
      <button class="btn btn-sm btn-outline-secondary me-1 ms-2" @click="shiftDate(-1)">‹</button>
      <input id="dayCalendar" type="text" class="form-control datepicker me-2" placeholder="Selecione a data"
        style="width: 180px;" />
      <button class="btn btn-sm btn-outline-secondary ms-1 me-2" @click="shiftDate(1)">›</button>
      <!-- <h2 class="mb-0">Agenda por Atendente {{ today }}</h2> -->
    </div>
    <AttendantsDayView :attendants="attendants" :appointments="appointments" :date="date" :today="today"
      @slot-click="onSlotClick" @appointment-click="onAppointmentClick" />

    <!-- Reutiliza o modal de criação/atualização existente -->
    <CreateScheduleModal :date="modalDate" :initial-time="modalInitialTime" :initial-category="modalInitialCategory"
      :initial-user="modalInitialUser" :initial-service="modalInitialService" />
    <UpdateScheduleModal />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import * as bootstrap from "bootstrap";
import AttendantsDayView from "../../components/Calendar/AttendantsDayView.vue";
import CreateScheduleModal from "./CreateScheduleModal.vue";
import UpdateScheduleModal from "./UpdateScheduleModal.vue";
import moment from 'moment';
import flatpickr from "flatpickr";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";

const STORAGE_KEY = "attendantsDay.selectedDate";

export default {
  name: "AttendantsDayPage",
  components: { AttendantsDayView, CreateScheduleModal, UpdateScheduleModal },
  data() {
    const savedDate = this.getSavedDate();
    const initialDate = savedDate || new Date().toISOString().slice(0, 10);

    return {
      today: moment(initialDate).format("DD/MM/YYYY"),
      date: initialDate,
      appointments: [],
      modalDate: initialDate,
      modalInitialTime: "",
      modalInitialCategory: null,
      modalInitialUser: null,
      modalInitialService: null,
      fp: null, // flatpickr instance
      desktopFp: null,
    };
  },
  computed: {
    ...mapGetters(["getCalendar", "getUsers"]),
    attendants() {
      // convert users from store to simple {id, name}
      const users = this.getUsers || [];
      if (!users || users.length === 0) {
        // fallback to empty list
        return [];
      }
      return users.map((u) => ({ id: u.id, name: u.name }));
    },
    dayEvents() {
      const events = Array.isArray(this.getCalendar) ? this.getCalendar : [];
      return events.filter((event) => event.date === this.date);
    },
    dayMetrics() {
      const blockedEvents = this.dayEvents.filter((event) => this.isBlockedEvent(event));
      const scheduledEvents = this.dayEvents.filter((event) => {
        return !this.isBlockedEvent(event) && !this.isCanceledEvent(event);
      });
      const clientKeys = scheduledEvents
        .map((event) => this.getEventClientKey(event))
        .filter(Boolean);

      return {
        totalAppointments: scheduledEvents.length,
        totalValue: scheduledEvents.reduce((sum, event) => sum + this.extractEventAmount(event), 0),
        uniqueClients: new Set(clientKeys).size,
        totalMinutes: scheduledEvents.reduce((sum, event) => sum + this.getEventDurationMinutes(event), 0),
        blockedCount: blockedEvents.length,
      };
    },
  },
  mounted() {
    const currentDate = this.parseIsoDate(this.date);
    const params = {
      user_id: "*",
      year: currentDate.getFullYear(),
      month: String(currentDate.getMonth() + 1).padStart(2, "0"),
    };

    // fetch users and calendar events using same flow as Calendar module
    this.fetchUsers(null).then(() => {
      this.fetchCalendar(params);
    });

    // fetch categories so CreateScheduleModal has data like in CalendarPage
    if (this.fetchCategories) {
      this.fetchCategories();
    }

    // fetch operating hours for the day view
    this.fetchOperatingHours();

    // initialize datepicker after DOM is ready
    this.initDatepicker();
    this.initDesktopDatepicker();
  },
  unmounted() {
    if (this.fp) {
      this.fp.destroy();
    }

    if (this.desktopFp) {
      this.desktopFp.destroy();
    }
  },
  watch: {
    // whenever calendar in store changes, map to local appointments for the selected date
    getCalendar(newVal) {
      if (!newVal || !Array.isArray(newVal)) return;
      const day = this.date;
      const items = newVal
        .filter((e) => e.date === day)
        .map((e) => {
          const attendantId = e.user_id || (e.user && e.user.id) || e.attendantId || null;
          const clientName = e.client && e.client.name ? e.client.name : e.client_name || "Sem cliente";
          const serviceName = e.service && e.service.name ? e.service.name : e.service_name || e.title || "Sem serviço";
          let title = serviceName;

          if (e.status == 'BLOCKED') {
            title = 'Bloqueio de agenda';
          }

          return {
            id: e.id,
            attendantId,
            start: e.start_time_service || e.start || null,
            end: e.end_time_service || e.end || null,
            title,
            clientName,
            serviceName,
            raw: e,
          };
        });

      this.appointments = items;
    },
    // keep picker widget in sync if date changes programmatically
    date(newVal) {
      // update label
      this.today = moment(newVal).format("DD/MM/YYYY");
      // keep picker widget in sync if date changes programmatically
      if (this.fp) {
        this.fp.setDate(newVal, false);
      }
      if (this.desktopFp) {
        this.desktopFp.setDate(newVal, false);
      }
      // recalc appointments for the new day using store data
      if (this.getCalendar && Array.isArray(this.getCalendar)) {
        const day = newVal;
        const items = this.getCalendar
          .filter((e) => e.date === day)
          .map((e) => {
            const attendantId = e.user_id || (e.user && e.user.id) || e.attendantId || null;
            const clientName = e.client && e.client.name ? e.client.name : e.client_name || "Sem cliente";
            const serviceName = e.service && e.service.name ? e.service.name : e.service_name || e.title || "Sem serviço";
            const title = e.status === 'BLOCKED' ? 'Bloqueio de agenda' : serviceName;

            return {
              id: e.id,
              attendantId,
              start: e.start_time_service || e.start || null,
              end: e.end_time_service || e.end || null,
              title,
              clientName,
              serviceName,
              raw: e,
            };
          });
        this.appointments = items;
      }
    },
  },
  methods: {
    ...mapActions(["fetchCalendar", "fetchUsers", "setCurrentCalendar", "fetchCategories", "fetchOperatingHours"]),
    isBlockedEvent(event) {
      return String(event && event.status ? event.status : "").toUpperCase() === "BLOCKED";
    },
    isCanceledEvent(event) {
      const status = String(event && event.status ? event.status : "").toUpperCase();
      return ["CANCELED", "CANCELLED", "CANCELADO", "CANCELADA"].includes(status);
    },
    getEventClientKey(event) {
      if (!event) return null;
      return event.client_id
        || (event.client && event.client.id)
        || (event.client && event.client.phone)
        || event.client_phone
        || event.client_name
        || (event.client && event.client.name)
        || null;
    },
    extractEventAmount(event) {
      if (!event) return 0;
      const candidates = [
        event.total,
        event.total_value,
        event.amount,
        event.amount_received,
        event.value,
        event.price,
        event.base_price,
        event.base_price_formatted,
        event.price_formatted,
        event.value_formatted,
        event.service && event.service.price,
        event.service && event.service.value,
        event.service && event.service.base_price,
      ];
      const amount = candidates
        .map((value) => this.parseCurrency(value))
        .find((value) => value > 0);

      return amount || 0;
    },
    parseCurrency(value) {
      if (value === null || value === undefined || value === "") return 0;
      if (typeof value === "number") return Number.isFinite(value) ? value : 0;

      const normalized = String(value).replace(/[^\d,.-]/g, "").trim();
      if (!normalized) return 0;

      if (normalized.includes(",") && normalized.includes(".")) {
        return Number(normalized.replace(/\./g, "").replace(",", ".")) || 0;
      }

      if (normalized.includes(",")) {
        return Number(normalized.replace(",", ".")) || 0;
      }

      return Number(normalized) || 0;
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(value || 0));
    },
    getEventDurationMinutes(event) {
      const start = event && (event.start_time_service || event.start);
      const end = event && (event.end_time_service || event.end);
      const startMinutes = this.timeToMinutes(start);
      const endMinutes = this.timeToMinutes(end);

      if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) {
        return 0;
      }

      return endMinutes - startMinutes;
    },
    timeToMinutes(value) {
      if (!value) return null;
      const match = String(value).match(/(\d{1,2}):(\d{2})/);
      if (!match) return null;
      const hours = Number(match[1]);
      const minutes = Number(match[2]);
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
      return hours * 60 + minutes;
    },
    formatDuration(totalMinutes) {
      const minutes = Number(totalMinutes || 0);
      if (minutes <= 0) return "0h";
      if (minutes < 60) return `${minutes}min`;

      const hours = Math.floor(minutes / 60);
      const remainder = minutes % 60;
      return remainder ? `${hours}h ${remainder}min` : `${hours}h`;
    },
    dayOfWeekText() {
      if (!this.date) return "";
      const d = this.parseIsoDate(this.date);
      const days = [
        "Domingo",
        "Segunda-feira",
        "Terça-feira",
        "Quarta-feira",
        "Quinta-feira",
        "Sexta-feira",
        "Sábado"
      ];
      return days[d.getDay()].toUpperCase();
    },
    getSavedDate() {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const isValid = /^\d{4}-\d{2}-\d{2}$/.test(saved);
          return isValid ? saved : null;
        }
      } catch (err) {
        console.warn("Não foi possível ler a data salva em localStorage", err);
      }
      return null;
    },
    saveDate(dateStr) {
      try {
        localStorage.setItem(STORAGE_KEY, dateStr);
      } catch (err) {
        console.warn("Não foi possível salvar a data em localStorage", err);
      }
    },
    parseIsoDate(isoDate) {
      const [year, month, day] = isoDate.split("-").map(Number);
      return new Date(year, month - 1, day);
    },
    formatIsoDate(dateObj) {
      return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}-${String(dateObj.getDate()).padStart(2, "0")}`;
    },
    initDatepicker() {
      this.fp = flatpickr("#dayCalendar", {
        locale: Portuguese,
        altInput: true,
        altFormat: "d/m/Y (l)",
        dateFormat: "Y-m-d",
        defaultDate: this.date,
        onChange: (selectedDates) => {
          if (selectedDates.length) {
            const iso = this.formatIsoDate(selectedDates[0]);
            this.onDateChange(iso);
          }
        },
      });
    },
    initDesktopDatepicker() {
      if (!this.$refs.desktopCalendar) {
        return;
      }

      this.desktopFp = flatpickr(this.$refs.desktopCalendar, {
        locale: Portuguese,
        inline: true,
        dateFormat: "Y-m-d",
        defaultDate: this.date,
        onChange: (selectedDates) => {
          if (selectedDates.length) {
            const iso = this.formatIsoDate(selectedDates[0]);
            this.onDateChange(iso);
          }
        },
      });
    },
    onDateChange(isoDate) {
      // update local state and label
      this.date = isoDate;
      this.today = moment(isoDate).format("DD/MM/YYYY");
      this.modalDate = isoDate;
      this.saveDate(isoDate);

      // always refetch calendar data for the new date's month/year
      const newD = this.parseIsoDate(isoDate);
      const params = {
        user_id: "*",
        year: newD.getFullYear(),
        month: String(newD.getMonth() + 1).padStart(2, "0"),
      };
      this.fetchCalendar(params);

      // refresh appointments for the new day (watch on getCalendar handles it)
    },
    onSlotClick(payload) {
      // payload: { attendantId, time }
      const attendantId = payload.attendantId;
      const time = payload.time;

      // find user info to prefill category/service if available
      const users = this.getUsers || [];
      const user = users.find((u) => String(u.id) === String(attendantId));

      let category = null;
      if (user) {
        category = user.category_id || (user.category && user.category.id) || (user.categories && user.categories[0] && user.categories[0].id) || null;
      }

      this.modalDate = this.date;
      this.modalInitialTime = time;
      this.modalInitialUser = attendantId;
      this.modalInitialCategory = category;
      this.modalInitialService = null;

      // open modal
      const modalEl = document.getElementById("createScheduleModal");
      if (modalEl) {
        const modal = new bootstrap.Modal(modalEl);
        modal.show();
      }
    },
    shiftDate(days) {
      const current = this.parseIsoDate(this.date);
      current.setDate(current.getDate() + days);
      const iso = this.formatIsoDate(current);
      this.onDateChange(iso);
    },
    onAppointmentClick(appt) {
      // appt.raw should be the original calendar object from the store
      const data = appt.raw || {};
      // seta no store para o modal reutilizar
      this.setCurrentCalendar(data);

      // abre o modal de update (mesma abordagem do CalendarPage)
      const modalEl = document.getElementById("updateScheduleModal");
      if (modalEl) {
        const modal = new bootstrap.Modal(modalEl);
        modal.show();
      }
    },
  },
};
</script>

<style scoped>
.page-attendants-day {
  padding: 0;
}

.page-attendants-day h2 {
  margin-bottom: 12px;
}

.attendants-day-date-sidebar {
  display: none;
}

@media (min-width: 1200px) {
  .page-attendants-day {
    display: grid;
    grid-template-columns: 286px minmax(0, 1fr);
    grid-template-rows: auto auto minmax(0, 1fr);
    height: 100vh;
    height: 100dvh;
    min-height: 0;
    overflow: hidden;
  }

  .attendants-day-date-sidebar {
    background: #ffffff;
    border-right: 1px solid #e9e7f1;
    display: block;
    grid-column: 1;
    grid-row: 1 / 4;
    height: 100%;
    overflow-y: auto;
    padding: 18px 16px;
  }

  .attendants-day-sidebar-title {
    color: #2b2a3f;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: 0.04em;
    margin-bottom: 14px;
    text-align: center;
  }

  .attendants-day-daily-metrics {
    border-top: 1px solid #e9e7f1;
    display: grid;
    gap: 8px;
    margin-top: 24px;
    padding-top: 18px;
  }

  .attendants-day-metrics-title {
    color: #2b2a3f;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.03em;
    margin-bottom: 2px;
    text-align: center;
  }

  .attendants-day-metric-card {
    background: #ffffff;
    border: 1px solid #ecebf4;
    border-radius: 7px;
    box-shadow: 0 6px 18px rgba(42, 38, 77, 0.04);
    overflow: hidden;
    padding: 11px 13px 11px 15px;
    position: relative;
  }

  .attendants-day-metric-card::before {
    background: rgba(101, 96, 240, 0.72);
    border-radius: 999px;
    bottom: 12px;
    content: "";
    left: 7px;
    position: absolute;
    top: 12px;
    width: 3px;
  }

  .attendants-day-metric-card--primary {
    background: linear-gradient(135deg, rgba(101, 96, 240, 0.08), #ffffff 70%);
    border-color: rgba(101, 96, 240, 0.18);
  }

  .attendants-day-metric-card span {
    color: #79788e;
    display: block;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    line-height: 1.2;
    margin-bottom: 6px;
    text-transform: uppercase;
  }

  .attendants-day-metric-card strong {
    color: #2b2a3f;
    display: block;
    font-size: 19px;
    font-weight: 900;
    line-height: 1.1;
  }

  .attendants-day-metrics-note {
    color: #79788e;
    font-size: 12px;
    margin: 2px 0 0;
    text-align: center;
  }

  .desktop-calendar-input {
    height: 0;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    width: 0;
  }

  .page-attendants-day > .attendants-day-weekday,
  .page-attendants-day > .attendants-day-date-controls,
  .page-attendants-day :deep(.attendants-day-wrap) {
    grid-column: 2;
    min-width: 0;
  }

  .page-attendants-day > .attendants-day-weekday {
    align-items: center;
    background: #ffffff;
    border-bottom: 1px solid #e9e7f1;
    display: none !important;
    justify-content: center !important;
    margin-bottom: 0 !important;
    min-height: 28px;
    padding: 4px 16px 0;
  }

  .page-attendants-day > .attendants-day-date-controls {
    background: #ffffff;
    border-bottom: 1px solid #e9e7f1;
    display: none !important;
    margin-bottom: 0 !important;
    min-height: 50px;
    padding: 8px 16px;
  }

  .page-attendants-day :deep(.attendants-day-wrap) {
    border-radius: 0;
    border-width: 0;
    grid-row: 1 / 4;
    height: 100%;
    min-height: 0;
  }

  .page-attendants-day :deep(.grid-wrap) {
    height: 100%;
    max-height: none;
  }

  .attendants-day-mini-calendar :deep(.flatpickr-calendar.inline) {
    background: transparent;
    border: 0;
    box-shadow: none;
    color: #2b2a3f;
    width: 100%;
  }

  .attendants-day-mini-calendar :deep(.flatpickr-months) {
    align-items: center;
    margin-bottom: 8px;
    border-radius: 7px;
  }

  .attendants-day-mini-calendar :deep(.flatpickr-months .flatpickr-month),
  .attendants-day-mini-calendar :deep(.flatpickr-current-month .flatpickr-monthDropdown-months),
  .attendants-day-mini-calendar :deep(.flatpickr-current-month input.cur-year) {
    background: transparent;
    color: #2b2a3f;
  }

  .attendants-day-mini-calendar :deep(.flatpickr-current-month .flatpickr-monthDropdown-months option) {
    background: #ffffff;
    color: #2b2a3f;
  }

  .attendants-day-mini-calendar :deep(.flatpickr-months .flatpickr-prev-month),
  .attendants-day-mini-calendar :deep(.flatpickr-months .flatpickr-next-month) {
    fill: #4c4a68;
    color: #4c4a68;
  }

  .attendants-day-mini-calendar :deep(.flatpickr-current-month) {
    font-size: 14px;
    font-weight: 700;
    padding-top: 0;
  }

  .attendants-day-mini-calendar :deep(.flatpickr-innerContainer),
  .attendants-day-mini-calendar :deep(.flatpickr-weekdays),
  .attendants-day-mini-calendar :deep(.flatpickr-weekdaycontainer) {
    background: transparent;
  }

  .attendants-day-mini-calendar :deep(.flatpickr-rContainer),
  .attendants-day-mini-calendar :deep(.flatpickr-days),
  .attendants-day-mini-calendar :deep(.dayContainer) {
    max-width: 100%;
    min-width: 100%;
    width: 100%;
  }

  .attendants-day-mini-calendar :deep(.flatpickr-weekday) {
    background: transparent;
    color: #79788e;
    font-size: 12px;
    font-weight: 700;
  }

  .attendants-day-mini-calendar :deep(.flatpickr-day) {
    border-radius: 6px;
    color: #2b2a3f;
    flex-basis: calc(100% / 7);
    height: 34px;
    line-height: 34px;
    margin: 1px 0;
    max-width: none;
  }

  .attendants-day-mini-calendar :deep(.flatpickr-day.prevMonthDay),
  .attendants-day-mini-calendar :deep(.flatpickr-day.nextMonthDay) {
    color: #a4a3b0;
  }

  .attendants-day-mini-calendar :deep(.flatpickr-day.selected),
  .attendants-day-mini-calendar :deep(.flatpickr-day.startRange),
  .attendants-day-mini-calendar :deep(.flatpickr-day.endRange) {
    background: var(--splash-primary-color);
    border-color: var(--splash-primary-color);
    color: #ffffff;
  }

  .attendants-day-mini-calendar :deep(.flatpickr-day.today:not(.selected)) {
    border-color: var(--splash-primary-color);
    color: var(--splash-primary-color);
  }

}

@media (max-width: 767px) {
  .page-attendants-day {
    display: flex;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    flex-direction: column;
  }

  .page-attendants-day :deep(.attendants-day-wrap) {
    flex: 1 1 auto;
    min-height: 0;
  }
}
</style>

<style>
@media (min-width: 1200px) {
  body:not(.dark) .page-attendants-day .attendants-day-mini-calendar .flatpickr-current-month .flatpickr-monthDropdown-months {
    background-color: transparent !important;
    color: #2b2a3f !important;
  }

  body:not(.dark) .page-attendants-day .attendants-day-mini-calendar .flatpickr-current-month .flatpickr-monthDropdown-months option {
    background-color: #ffffff !important;
    color: #2b2a3f !important;
  }

  body.dark .page-attendants-day .attendants-day-date-sidebar {
    background: #34334a !important;
    border-right-color: #45445f !important;
  }

  body.dark .page-attendants-day .attendants-day-sidebar-title {
    color: #ffffff !important;
  }

  body.dark .page-attendants-day .attendants-day-daily-metrics {
    border-top-color: #45445f !important;
  }

  body.dark .page-attendants-day .attendants-day-metrics-title {
    color: #ffffff !important;
  }

  body.dark .page-attendants-day .attendants-day-metric-card {
    background: #2b2a3f !important;
    border-color: #45445f !important;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.14) !important;
  }

  body.dark .page-attendants-day .attendants-day-metric-card::before {
    background: rgba(143, 140, 255, 0.82) !important;
  }

  body.dark .page-attendants-day .attendants-day-metric-card--primary {
    background: linear-gradient(135deg, rgba(101, 96, 240, 0.22), #2b2a3f 72%) !important;
    border-color: rgba(101, 96, 240, 0.35) !important;
  }

  body.dark .page-attendants-day .attendants-day-metric-card span,
  body.dark .page-attendants-day .attendants-day-metrics-note {
    color: #bcbacb !important;
  }

  body.dark .page-attendants-day .attendants-day-metric-card strong {
    color: #ffffff !important;
  }

  body.dark .page-attendants-day .attendants-day-weekday {
    background: #ffffff !important;
    border-bottom-color: #e9e7f1 !important;
    color: #2b2a3f !important;
  }

  body.dark .page-attendants-day > .attendants-day-date-controls {
    background: #ffffff !important;
    border-bottom-color: #e9e7f1 !important;
  }

  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-calendar.inline,
  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-months .flatpickr-month,
  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-current-month .flatpickr-monthDropdown-months,
  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-current-month input.cur-year {
    background: transparent !important;
    color: #ffffff !important;
  }

  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-months .flatpickr-prev-month,
  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-months .flatpickr-next-month {
    color: #bcbacb !important;
    fill: #bcbacb !important;
  }

  body.dark .flatpickr-months{
    background-color: #2b2a3f !important;
  }

  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-weekday {
    color: #bcbacb !important;
  }

  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-day {
    color: #ffffff !important;
  }

  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-day:hover,
  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-day:focus {
    background: #45445f !important;
    border-color: #45445f !important;
  }

  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-day.prevMonthDay,
  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-day.nextMonthDay {
    color: #85839a !important;
  }

  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-day.selected,
  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-day.startRange,
  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-day.endRange {
    background: var(--splash-primary-color) !important;
    border-color: var(--splash-primary-color) !important;
    color: #ffffff !important;
  }

  body.dark .page-attendants-day .attendants-day-mini-calendar .flatpickr-day.today:not(.selected) {
    border-color: #8f8cff !important;
    color: #c8c6ff !important;
  }
}
</style>
